import { create } from 'zustand';
import type { BaseGameState, ConnectionStatus } from '@platform/contracts';
import type {
  ActiveTab,
  AutoPlayProgress,
  BetBoxState,
  BetMode,
  BetSide,
  BetStatus,
  ContainerRef,
  DeviceType,
  HistoryItem,
  HoverIdx,
  NotificationMessage,
  Phase,
  PlayerBet,
} from '@platform/contracts';
import type { AutoPlaySettings } from '@core/UI/overlayelements/AutoPlayModal';
import { formatCentsToCurrency } from '@core/utils/CurrencyFormatter';
import { AssetManager } from '@core/Assets/AssetStore';
import { PHASE_CONFIG } from '@core/gameengine/config/PhaseConfig';
import {
  type GameBetLimits,
  resolveGameBetLimits,
  clampStake,
  clampAutoCashout,
} from '@core/gameengine/config/GameBetLimits';
import { getRegulatoryPolicy } from '@core/regulatory';
import { track } from '@core/analytics';

export type { GameBetLimits } from '@core/gameengine/config/GameBetLimits';
export {
  DEFAULT_GAME_BET_LIMITS,
  resolveGameBetLimits,
  computeMaxWinPerBet,
  clampStake,
  clampAutoCashout,
  formatGameLimit,
} from '@core/gameengine/config/GameBetLimits';

export type {
  ActiveTab,
  AutoPlayProgress,
  BetBoxState,
  BetMode,
  BetSide,
  BetStatus,
  ContainerRef,
  DeviceType,
  HistoryItem,
  HoverIdx,
  NotificationMessage,
  Phase,
  PlayerBet,
} from '@platform/contracts';

export interface CrashGameStoreConfig {
  gameId: number;
  tenantId: string;
  tableId: string;
  slug: string;
  /** Override in game `configs/GameConfig.ts` — single place to tune limits. */
  betLimits?: Partial<GameBetLimits>;
  /**
   * When true, identical error toasts are replaced on each trigger instead of
   * being suppressed by the notification debounce window (skyx_new).
   */
  repeatErrorNotifications?: boolean;
  /**
   * When true, error toasts always stack (like cashout success) instead of
   * being deduplicated or replaced.
   */
  stackErrorNotifications?: boolean;
}

export interface CrashState extends BaseGameState {
  slug: string;
  betLimits: GameBetLimits;
  phase: Phase;
  phaseTotalDuration: number;
  phaseRemainingDuration: number;
  phaseDurationsAnchoredAt: number;
  nowX: number;
  crashedAtX: number | null;
  currency: string;
  leftPanelWidth: number;
  containerRef: ContainerRef;
  windowWidth: number;
  isLoading: boolean;
  isLandscape: boolean;
  isBetbtnHover: boolean;
  isBetbtnPress: boolean;
  isIpad: boolean;
  betMode: BetMode;
  deviceType: DeviceType;
  isAuto: boolean;
  betAmount1: number;
  betAmount2: number;
  autoCashout1: boolean;
  autoCashout2: boolean;
  activeTab: ActiveTab;
  hoverIdx: HoverIdx;
  balance: number;
  bets: Record<BetSide, BetBoxState>;
  allBets: PlayerBet[];
  history: HistoryItem[];
  notifications: NotificationMessage[];
  expiring: number[];
  activePlayersCount: number;
  totalCashoutAmount: number;
  activeBetsCount: number;
  totalBetsCount: number;
  cancelledBetNos: number[];
  cancelledBetStakes: Array<{ stake: number; timestamp: number }>;
  provablyFairModal: {
    isOpen: boolean;
    roundData: HistoryItem | null;
  };
  isAutoPlayModalOpen: boolean;
  autoPlayModalSide: BetSide | null;
  autoPlaySettings: Record<BetSide, AutoPlaySettings | null>;
  autoPlayProgress: Record<BetSide, AutoPlayProgress | null>;
  assetManager: AssetManager | null;
  isScoreboardExpanded: boolean;
  setAssetManager: (assetManager: AssetManager) => void;
  setPhase: (phase: string) => void;
  setPhaseDurations: (total: number, remaining: number) => void;
  setTick: (x: number, ts: number) => void;
  setFinalX: (finalX: number) => void;
  setCurrency: (currency: string) => void;
  setAutoCashout1: (autoCashout1: boolean) => void;
  setAutoCashout2: (autoCashout2: boolean) => void;
  setWindowWidth: (windowWidth: number) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsLandscape: (isLandscape: boolean) => void;
  setIsBetbtnHover: (isBetbtnHover: boolean) => void;
  setIsBetbtnPress: (isBetbtnPress: boolean) => void;
  setIsIPad: (isIpad: boolean) => void;
  setBetMode: (betMode: BetMode) => void;
  setDeviceType: (deviceType: DeviceType) => void;
  setContainerRef: (containerRef: ContainerRef) => void;
  setLeftPanelWidth: (leftPanelWidth: number) => void;
  setIsAuto: (isAuto: boolean) => void;
  setBetAmount1: (betAmount1: number) => void;
  setBetAmount2: (betAmount2: number) => void;
  setActiveTab: (activeTab: ActiveTab) => void;
  setHoverIdx: (hoverIdx: HoverIdx) => void;
  setBalance: (balance: number) => void;
  setBetLimits: (overrides: Partial<GameBetLimits>) => void;
  upBet: (side: BetSide, patch: Partial<BetBoxState>) => void;
  assignBetId: (side: BetSide) => number;
  resetForNextRound: (summaryData?: Partial<HistoryItem>) => void;
  /**
   * Reset bet slots after a round ends.
   * When `keepPendingCashouts` is true (FLEW_AWAY), preserve `cashing`/`cashed`
   * slots so a late CASHOUT ACK can still resolve and show the win popup.
   */
  updateBetStatusesForRoundEnd: (opts?: {
    keepPendingCashouts?: boolean;
  }) => void;
  addPlayerBet: (bet: PlayerBet) => void;
  updatePlayerBet: (betNo: number, patch: Partial<PlayerBet>) => void;
  removePlayerBet: (betNo: number, stake?: number) => void;
  addNotification: (notification: Omit<NotificationMessage, 'id'>) => void;
  removeNotification: (id: number) => void;
  openProvablyFairModal: (roundData: HistoryItem) => void;
  closeProvablyFairModal: () => void;
  addHistory: (item: HistoryItem) => void;
  setHistory: (items: HistoryItem[]) => void;
  setServerBets: (serverBets: any[]) => void;
  setRoundStats: (stats: {
    activePlayers?: number;
    totalCashout?: number;
    activeBets?: number;
    totalBets?: number;
  }) => void;
  openAutoPlayModal: (side: BetSide) => void;
  closeAutoPlayModal: () => void;
  startAutoPlay: (settings: AutoPlaySettings, side: BetSide) => void;
  stopAutoPlay: (side: BetSide) => void;
  updateAutoPlayProgress: (
    side: BetSide,
    progress: Partial<AutoPlayProgress>
  ) => void;
  setScoreboardExpanded: (val: boolean) => void;
  /**
   * Sequential table round number from ROUND_UPDATE (`p.roundNo`).
   * Distinct from `roundId`, which is the server's timestamp-style round id.
   */
  roundNo?: number;
  setRoundNo: (roundNo: number) => void;
}

const initialBetState = (limits: GameBetLimits): BetBoxState => ({
  stake: limits.defaultStake,
  autoCashoutX: limits.defaultAutoCashout,
  autoCashoutEnabled: false,
  status: 'idle',
  betMode: 'bet',
  isConfirmed: false,
});

export function createCrashGameStore(config: CrashGameStoreConfig) {
  const betLimits = resolveGameBetLimits(config.betLimits);
  const repeatErrorNotifications = config.repeatErrorNotifications ?? false;
  const stackErrorNotifications = config.stackErrorNotifications ?? false;

  return create<CrashState>()((set, get) => ({
    gameId: config.gameId,
    tenantId: config.tenantId,
    tableId: config.tableId,
    slug: config.slug,
    betLimits,
    roundId: undefined,
    roundNo: undefined,
    phase: 'WAITING',
    connectionStatus: 'idle',
    lastMessage: undefined,
    roundStartTime: undefined,
    phaseTotalDuration: PHASE_CONFIG.WAITING.defaultDuration,
    phaseRemainingDuration: PHASE_CONFIG.WAITING.defaultDuration,
    phaseDurationsAnchoredAt: Date.now(),
    nowX: 1.0,
    crashedAtX: null,
    currency: 'USD',
    balance: 0,
    bets: {
      A: initialBetState(betLimits),
      B: initialBetState(betLimits),
    },
    allBets: [],
    history: [],
    leftPanelWidth: 0,
    containerRef: null,
    windowWidth: window.innerWidth,
    isLoading: true,
    isBetbtnPress: false,
    isBetbtnHover: false,
    isLandscape: window.innerWidth > window.innerHeight,
    betMode: 'bet',
    deviceType: 'desktop',
    isIpad: false,
    betAmount1: 1.0,
    isAuto: false,
    betAmount2: 1.0,
    autoCashout1: false,
    autoCashout2: false,
    activeTab: 'all',
    hoverIdx: null,
    isScoreboardExpanded: false,
    notifications: [],
    expiring: [],
    activePlayersCount: 0,
    totalCashoutAmount: 0,
    activeBetsCount: 0,
    totalBetsCount: 0,
    cancelledBetNos: [],
    cancelledBetStakes: [],
    provablyFairModal: {
      isOpen: false,
      roundData: null,
    },
    isAutoPlayModalOpen: false,
    autoPlayModalSide: null,
    autoPlaySettings: { A: null, B: null },
    autoPlayProgress: { A: null, B: null },
    assetManager: null,
    setAssetManager: assetManager => set({ assetManager }),
    setPhase: phase => set({ phase: phase as Phase }),
    setPhaseDurations: (total, remaining) =>
      set({
        phaseTotalDuration: total,
        phaseRemainingDuration: remaining,
        phaseDurationsAnchoredAt: Date.now(),
      }),
    setTick: (x, _ts) => set({ nowX: x }),
    setFinalX: finalX => set({ crashedAtX: finalX }),
    setCurrency: currency => set({ currency }),
    setIsAuto: isAuto => set({ isAuto }),
    setBetAmount1: betAmount1 => set({ betAmount1 }),
    setBetMode: betMode => set({ betMode }),
    setDeviceType: deviceType => set({ deviceType }),
    setBetAmount2: betAmount2 => set({ betAmount2 }),
    setLeftPanelWidth: leftPanelWidth => set({ leftPanelWidth }),
    setWindowWidth: windowWidth => set({ windowWidth }),
    setContainerRef: containerRef => set({ containerRef }),
    setIsIPad: isIpad => set({ isIpad }),
    setIsLoading: isLoading => set({ isLoading }),
    setIsLandscape: isLandscape => set({ isLandscape }),
    setIsBetbtnHover: isBetbtnHover => set({ isBetbtnHover }),
    setIsBetbtnPress: isBetbtnPress => set({ isBetbtnPress }),
    setAutoCashout1: autoCashout1 => set({ autoCashout1 }),
    setAutoCashout2: autoCashout2 => set({ autoCashout2 }),
    setActiveTab: activeTab => set({ activeTab }),
    setHoverIdx: hoverIdx => set({ hoverIdx }),
    setScoreboardExpanded: val => set({ isScoreboardExpanded: val }),
    setBalance: balance => set({ balance }),
    setBetLimits: overrides =>
      set(state => {
        const nextLimits = resolveGameBetLimits({
          ...state.betLimits,
          ...overrides,
        });
        const nextBets = { ...state.bets };
        (Object.keys(nextBets) as BetSide[]).forEach(side => {
          const bet = nextBets[side];
          nextBets[side] = {
            ...bet,
            stake: clampStake(bet.stake, nextLimits),
            autoCashoutX:
              bet.autoCashoutX != null
                ? clampAutoCashout(bet.autoCashoutX, nextLimits)
                : null,
          };
        });
        return { betLimits: nextLimits, bets: nextBets };
      }),
    setRound: (roundId: string) => set({ roundId }),
    setRoundNo: (roundNo: number) => set({ roundNo }),
    setConnectionStatus: (connectionStatus: ConnectionStatus) =>
      set({ connectionStatus }),
    setLastMessage: (lastMessage: string) => set({ lastMessage }),
    addHistory: item => {
      set(state => ({
        history: [item, ...state.history].slice(0, 60),
      }));
    },
    setHistory: items => {
      set({ history: items.slice(0, 60) });
    },
    upBet: (side, patch) => {
      set(state => ({
        bets: {
          ...state.bets,
          [side]: { ...state.bets[side], ...patch },
        },
      }));
    },
    assignBetId: side => {
      const { bets } = get();
      const usedBetNos = new Set(
        Object.values(bets)
          .map(b => b.betNo)
          .filter((n): n is number => n !== undefined && n > 0)
      );
      for (let i = 1; i <= 2; i++) {
        if (!usedBetNos.has(i)) {
          const availableBetNo = i;
          set(state => ({
            bets: {
              ...state.bets,
              [side]: { ...state.bets[side], betNo: availableBetNo },
            },
          }));
          return availableBetNo;
        }
      }
      console.error('No available bet numbers to assign.');
      return 0;
    },
    updateBetStatusesForRoundEnd: opts =>
      set(state => {
        const nextBets = { ...state.bets };
        const keepPendingCashouts = opts?.keepPendingCashouts === true;
        (Object.keys(nextBets) as BetSide[]).forEach(side => {
          const currentBet = nextBets[side];

          // Keep pending / completed cashouts intact during FLEW_AWAY so a late
          // CASHOUT ACK can still find betNo and show the win popup.
          if (
            keepPendingCashouts &&
            (currentBet.status === 'cashing' || currentBet.status === 'cashed')
          ) {
            nextBets[side] = { ...currentBet };
            return;
          }

          const isAutoplayActive =
            getRegulatoryPolicy().autoplay !== false &&
            state.autoPlaySettings[side] !== null;
          let nextStatus: BetStatus = 'idle';
          if (currentBet.status === 'armed') {
            nextStatus = 'armed';
          } else if (
            isAutoplayActive &&
            (currentBet.status === 'placed' ||
              currentBet.status === 'cashed' ||
              currentBet.status === 'cashing' ||
              currentBet.status === 'lost' ||
              currentBet.status === 'rejected')
          ) {
            nextStatus = 'armed';
          }
          nextBets[side] = {
            ...initialBetState(state.betLimits ?? betLimits),
            stake: currentBet.stake,
            autoCashoutX: currentBet.autoCashoutX,
            autoCashoutEnabled: currentBet.autoCashoutEnabled,
            betMode: currentBet.betMode,
            status: nextStatus,
          };
        });
        return { bets: nextBets };
      }),
    resetForNextRound: summaryData => {
      get().updateBetStatusesForRoundEnd();
      set(state => {
        const newHistory =
          summaryData?.bust && summaryData?.roundId
            ? [summaryData as HistoryItem, ...state.history]
            : state.history;
        const trimmedHistory =
          newHistory.length > 50 || summaryData?.bust
            ? newHistory.slice(0, 50)
            : newHistory; // Fixed: always use newHistory, not state.history

        // Diagnostic logging every 5 rounds to track state growth (random crash investigation)
        const currentRound = newHistory.length;
        if (currentRound % 5 === 0 || currentRound <= 5) {
          console.warn(`[Round ${currentRound}] Reset diagnostics:`, {
            notifications: state.notifications.length,
            allBets: state.allBets.length,
            cancelledBetNos: state.cancelledBetNos.length,
            cancelledBetStakes: state.cancelledBetStakes.length,
            history: newHistory.length,
            trimmed: trimmedHistory.length,
          });
        }

        return {
          ...state,
          phase: 'WAITING' as Phase,
          nowX: 1.0,
          crashedAtX: summaryData?.bust ?? null,
          allBets: [],
          history: trimmedHistory,
          activePlayersCount: 0,
          totalCashoutAmount: 0,
          activeBetsCount: 0,
          totalBetsCount: 0,
          cancelledBetNos: [],
          cancelledBetStakes: [],
          // Keep cashout success toasts across the round boundary so a late
          // CASHOUT ACK popup is not wiped when WAITING starts. Errors clear.
          notifications: state.notifications.filter(n => n.type === 'success'),
          expiring: state.expiring.filter(id =>
            state.notifications.some(n => n.id === id && n.type === 'success')
          ),
        };
      });
    },
    addPlayerBet: bet =>
      set(state => ({
        allBets: [...state.allBets, bet],
      })),
    updatePlayerBet: (betNo, patch) =>
      set(state => ({
        allBets: state.allBets.map(b =>
          b.betNo === betNo ? { ...b, ...patch } : b
        ),
      })),
    removePlayerBet: (betNo, stake) => {
      set(state => {
        const now = Date.now();
        const updates: Partial<CrashState> = {
          allBets: state.allBets.filter(b => b.betNo !== betNo),
          cancelledBetNos: [...state.cancelledBetNos, betNo],
        };
        if (stake !== undefined) {
          // Purge stale stakes (older than 10s) to prevent unbounded array growth
          const purgedStakes = state.cancelledBetStakes.filter(
            s => now - s.timestamp < 10000
          );
          updates.cancelledBetStakes = [
            ...purgedStakes,
            { stake, timestamp: now },
          ];
        }
        return updates;
      });
    },
    addNotification: notification => {
      const now = Date.now();
      set(state => {
        const isSameNotification = (
          a: NotificationMessage,
          b: Omit<NotificationMessage, 'id'>
        ) =>
          a.type === b.type &&
          a.message === b.message &&
          a.multiplier === b.multiplier &&
          a.winAmount === b.winAmount &&
          a.betSide === b.betSide;

        // Enforce notification cap to prevent memory leak on mobile
        const MAX_PENDING_NOTIFICATIONS = 20;
        let workingNotifications = state.notifications;
        let workingExpiring = state.expiring;

        if (state.notifications.length >= MAX_PENDING_NOTIFICATIONS) {
          // Auto-purge oldest half to prevent unbounded growth
          const toRemove = state.notifications.slice(
            0,
            Math.floor(MAX_PENDING_NOTIFICATIONS / 2)
          );
          const idsToRemove = new Set(toRemove.map(n => n.id));

          // Filter out old notifications (proper immutable update, not direct mutation)
          workingNotifications = state.notifications.filter(
            n => !idsToRemove.has(n.id)
          );
          workingExpiring = state.expiring.filter(id => !idsToRemove.has(id));
        }

        if (stackErrorNotifications && notification.type === 'error') {
          // during preloader) does not show two toasts for one click.
          const stackDebounceWindowMs = 2000;
          const isDuplicateRecent = workingNotifications.some(
            n =>
              isSameNotification(n, notification) &&
              now - n.id < stackDebounceWindowMs
          );
          if (isDuplicateRecent) {
            return state;
          }
          const newNotification = { ...notification, id: now };
          setTimeout(() => get().removeNotification(newNotification.id), 4000);
          return {
            ...state,
            notifications: [...workingNotifications, newNotification],
            expiring: workingExpiring,
          };
        }

        if (repeatErrorNotifications && notification.type === 'error') {
          const removedIds = new Set(
            workingNotifications
              .filter(n => isSameNotification(n, notification))
              .map(n => n.id)
          );
          const newNotification = { ...notification, id: now };
          setTimeout(() => get().removeNotification(newNotification.id), 4000);
          return {
            ...state,
            notifications: [
              ...workingNotifications.filter(n => !removedIds.has(n.id)),
              newNotification,
            ],
            expiring: workingExpiring.filter(id => !removedIds.has(id)),
          };
        }

        const last = workingNotifications[workingNotifications.length - 1];
        if (last && isSameNotification(last, notification)) {
          return state;
        }
        const debounceWindowMs = 2000;
        for (let i = workingNotifications.length - 1; i >= 0; i--) {
          const n = workingNotifications[i];
          if (isSameNotification(n, notification)) {
            if (now - n.id < debounceWindowMs) {
              return state;
            }
            break;
          }
        }
        const newNotification = { ...notification, id: now };
        setTimeout(() => get().removeNotification(newNotification.id), 4000);
        return {
          ...state,
          notifications: [...workingNotifications, newNotification],
          expiring: workingExpiring,
        };
      });
    },
    removeNotification: id => {
      set(state => {
        if (!state.expiring.includes(id)) {
          return {
            ...state,
            expiring: [...state.expiring, id],
          };
        }
        return state;
      });
      setTimeout(() => {
        set(state => ({
          ...state,
          notifications: state.notifications.filter(n => n.id !== id),
          expiring: state.expiring.filter(expId => expId !== id),
        }));
      }, 500);
    },
    openProvablyFairModal: roundData =>
      set({
        provablyFairModal: { isOpen: true, roundData },
      }),
    closeProvablyFairModal: () =>
      set(state => ({
        provablyFairModal: { ...state.provablyFairModal, isOpen: false },
      })),
    setServerBets: serverBets => {
      set(state => {
        const now = Date.now();
        const filteredBets = serverBets
          .filter((entry: any) => {
            const betId = entry.betId || entry.betNo || entry.id;
            const entryStake = formatCentsToCurrency(entry.bet);
            if (betId && state.cancelledBetNos.includes(betId)) {
              return false;
            }
            const matchingCancelledStake = state.cancelledBetStakes.find(
              cancelled =>
                cancelled.stake === entryStake &&
                now - cancelled.timestamp < 5000
            );
            if (matchingCancelledStake) {
              return false;
            }
            return true;
          })
          .map((entry: any) => ({
            player: entry.nickname,
            stake: formatCentsToCurrency(entry.bet),
            betNo: entry.betId || entry.betNo || entry.id || -1,
            cashedAtX:
              entry.status === 'CASHED_OUT' ? entry.cashoutMul : undefined,
            win:
              entry.status === 'CASHED_OUT'
                ? formatCentsToCurrency(entry.cashoutAmount)
                : undefined,
          }));
        return {
          ...state,
          allBets: filteredBets,
        };
      });
    },
    setRoundStats: stats => {
      set(state => ({
        ...state,
        activePlayersCount: stats.activePlayers ?? state.activePlayersCount,
        totalCashoutAmount: stats.totalCashout ?? state.totalCashoutAmount,
        activeBetsCount: stats.activeBets ?? state.activeBetsCount,
        totalBetsCount: stats.totalBets ?? state.totalBetsCount,
      }));
    },
    openAutoPlayModal: side => {
      if (getRegulatoryPolicy().autoplay === false) return;
      set({ isAutoPlayModalOpen: true, autoPlayModalSide: side });
    },
    closeAutoPlayModal: () => {
      set({ isAutoPlayModalOpen: false });
    },
    startAutoPlay: (settings, side) => {
      if (getRegulatoryPolicy().autoplay === false) return;
      track('autospin_clicked', { enabled: true, side });
      track('autoplay_started', { side });
      set(state => ({
        autoPlaySettings: {
          ...state.autoPlaySettings,
          [side]: settings,
        },
        autoPlayProgress: {
          ...state.autoPlayProgress,
          [side]: {
            roundsPlayed: 0,
            netProfitLoss: 0,
          },
        },
        isAutoPlayModalOpen: false,
      }));
    },
    stopAutoPlay: side => {
      if (get().autoPlaySettings[side] !== null) {
        track('autoplay_stopped', { side });
      }
      set(state => {
        const currentBet = state.bets[side];
        const updates = {
          autoPlaySettings: {
            ...state.autoPlaySettings,
            [side]: null,
          },
          autoPlayProgress: {
            ...state.autoPlayProgress,
            [side]: null,
          },
        };
        // Stay on the Auto tab and keep Auto Cash Out as-is. Only cancel an
        // armed next-round bet so stopping Auto Play does not place it.
        if (currentBet.status !== 'armed') {
          return updates;
        }
        return {
          ...updates,
          bets: {
            ...state.bets,
            [side]: {
              ...currentBet,
              status: 'idle',
              cashedAtX: undefined,
              win: undefined,
              error: '',
            },
          },
        };
      });
    },
    updateAutoPlayProgress: (side, patch) => {
      set(state => {
        const currentProgress = state.autoPlayProgress[side];
        if (!currentProgress) return state;
        return {
          autoPlayProgress: {
            ...state.autoPlayProgress,
            [side]: {
              ...currentProgress,
              ...patch,
            },
          },
        };
      });
    },
  }));
}
